import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/rest";
import type {
  FlowWindow,
  ParticipantCohortKind,
  ParticipantsStats,
} from "../api/types";
import {
  FLOW_REFRESH_MS,
  flowRefresher,
  keepsPreviousSnapshot,
  nextPollMs,
} from "../lib/participants";
import { setParticipantsRefresher } from "../store/dispatch";

/**
 * Накопленные позиции участников рынка.
 *
 * Опрос REST, а не сокет, по той же причине, что у счётчика ленты: это
 * СОСТОЯНИЕ («кто сколько набрал»), а не поток, где важен порядок.
 *
 * Окно статистики входит в КЛЮЧ, а не только в запрос: своды за сутки и за месяц
 * — разные данные, и общий ключ показал бы месячные числа под подписью «сегодня»
 * до прихода нового ответа. Снимок рынка от переключения не перечитывается — на
 * той стороне он кешируется отдельно от статистики кошельков.
 */
export function participantsKey(
  conditionID: string | undefined,
  flow: FlowWindow | undefined,
  against: boolean,
  tags: readonly string[] = [],
  notags: readonly string[] = [],
  groups: readonly ParticipantCohortKind[] = [],
  notgroups: readonly ParticipantCohortKind[] = [],
) {
  // Пустое окно — «умолчание сервера», и в ключе оно СВОЁ значение, а не одно из
  // пяти: ответ на такой запрос подпишется тем, что выбрал сервер.
  //
  // Режим входит в ключ по той же причине, что и окно, только резче: «против нас»
  // отдаёт ПОДМНОЖЕСТВО рынка, и общий ключ на полсекунды показал бы три строки
  // под подписью «весь рынок» — или наоборот, что хуже: сотню чужих кошельков как
  // наших контрагентов.
  //
  // Фильтр по пометкам входит в ключ по той же причине, и она здесь сильнее
  // всего: он уезжает НА СЕРВЕР и меняет состав строк. Общий ключ показал бы
  // отфильтрованную сотню под подписью «весь рынок» — или наоборот. Списки
  // сортируются: «кит,картель» и «картель,кит» — один и тот же вопрос, и два
  // ключа под него означали бы два запроса и два кэша одного ответа.
  //
  // Фильтр по подгруппам — там же и по тому же доводу: он тоже уезжает на сервер
  // и тоже меняет состав строк.
  return [
    "participants",
    conditionID ?? "",
    flow ?? "",
    against,
    [...tags].sort().join(","),
    [...notags].sort().join(","),
    [...groups].sort().join(","),
    [...notgroups].sort().join(","),
  ] as const;
}

export function useParticipants(
  conditionID: string | undefined,
  flow: FlowWindow | undefined,
  against: boolean,
  tags: readonly string[] = [],
  notags: readonly string[] = [],
  groups: readonly ParticipantCohortKind[] = [],
  notgroups: readonly ParticipantCohortKind[] = [],
) {
  return useQuery<ParticipantsStats>({
    queryKey: participantsKey(
      conditionID,
      flow,
      against,
      tags,
      notags,
      groups,
      notgroups,
    ),
    queryFn: () =>
      api
        .participants(
          conditionID ?? "",
          flow,
          against,
          tags,
          notags,
          groups,
          notgroups,
        )
        .then((r) => r.participants),
    enabled: Boolean(conditionID),
    // Прошлый свод остаётся на экране, пока едет новый, — но ТОЛЬКО в пределах
    // одного рынка. Переключение окна статистики или режима не должно мигать
    // пустой таблицей: строки «в этом окне» от них не зависят вовсе. А вот при
    // переходе на другой рынок держать прошлый свод нельзя — направление, роль и
    // средняя цена считаются по токенам НОВОГО окна, и на экране оказывается
    // гибрид двух рынков. Разбор и замер — в lib/participants.ts.
    placeholderData: (prev, prevQuery) =>
      keepsPreviousSnapshot(prevQuery?.queryKey, conditionID) ? prev : undefined,
    // Темп задаёт СЕРВЕР — полем next_in_ms в ответе, а не константа здесь.
    //
    // Дело не в удобстве: срок годности снимка на той стороне плавает по цене
    // обхода (пустое пятиминутное окно стоит трёх запросов к Data API, полное
    // окно BTC — дюжины), так что константы, которую можно было бы держать
    // здесь, попросту не существует. Пока она была, два таймера тикали с
    // независимыми фазами, и замер 2026-08-11 показал, чего это стоит на экране:
    // возраст данных пилой рос 2 → 19 → 36 → 52 секунды на НЕИЗМЕННОМ снимке.
    //
    // Правило «не чаще, чем меняется источник» никуда не делось — оно просто
    // считается там, где известна цена запроса. Расчёт задержки живёт в
    // lib/participants.ts, потому что тесты есть у lib, а не у хуков.
    refetchInterval: (q) => nextPollMs(q.state.data?.next_in_ms),
    // Опрашиваем и в НЕВИДИМОЙ вкладке. Прежде здесь стояло false с доводом
    // «обход истории рынка ради окна, на которое никто не смотрит, — дюжина
    // запросов ни за чем», и довод оказался неверен дважды.
    //
    // ПО ЦЕНЕ. Дюжину запросов к чужому индексатору стоит ОБХОД, а не наш
    // запрос: сервер держит снимок рынка в кеше со сроком годности 5–15 с
    // (webapi.participantsTTLMin/Max) и обходит источник раз в этот срок,
    // сколько бы вкладок его ни спрашивало. Лишний опрос попадает в тёплый кеш
    // и стоит одного SELECT. Именно поэтому такт и присылает сервер полем
    // next_in_ms — он один знает, когда снимок протух.
    //
    // ПО СМЫСЛУ. «Невидимая вкладка» и «никто не смотрит» — разные вещи.
    // Просмотр идёт в том числе через встроенный предпросмотр IDE, где документ
    // ВСЕГДА hidden: панель смотрят непрерывно, а опрос стоял, и числа
    // обновлялись только в момент переключения вкладки. Отличить один случай от
    // другого браузер не даёт, и выбирать надо тот, в котором ошибка дешевле:
    // лишний SELECT против таблицы, молча показывающей позавчерашнее
    // накопление на терминале с реальными деньгами.
    //
    // Ленивую подгрузку сделок кошелька (useMarketWalletTrades) это НЕ касается:
    // за неё платит чужой лимитер поштучно, и там false стоит по-прежнему.
    refetchIntervalInBackground: true,
  });
}

/**
 * Подписывает перечитывание накопления на стук трейдера «окно изменилось».
 *
 * Ставится один раз на приложение, как usePositionsRefreshOnFill, и по тому же
 * доводу: инвалидация по чужому ключу дешёвая — неактивный запрос TanStack
 * Query не перезапрашивает, — а подписка, живущая вместе с панелью, теряла бы
 * стуки на перемонтировании.
 *
 * Ключ инвалидации — префикс ["participants", рынок]: под ним лежат все
 * варианты свода (окна статистики, режимы, фильтры), и перечитывается тот,
 * который сейчас на экране. Опрос по next_in_ms остаётся страховкой на
 * потерянный стук и на трейдер прежней версии, который не стучит.
 *
 * Стук, не новее уже полученного ответа (epoch/seq в теле), панель не
 * перечитывает: сервер отдал бы то же тело. Разбор — flowNoticeIsNews.
 */
export function useParticipantsRefreshOnFlow(): void {
  const client = useQueryClient();
  useEffect(() => {
    const refresh = flowRefresher(
      (conditionID) =>
        client
          .getQueriesData<ParticipantsStats>({ queryKey: ["participants", conditionID] })
          .map(([, data]) => data),
      (conditionID) => {
        void client.invalidateQueries({ queryKey: ["participants", conditionID] });
      },
      FLOW_REFRESH_MS,
    );
    setParticipantsRefresher(refresh.fire);
    return () => {
      setParticipantsRefresher(null);
      refresh.dispose();
    };
  }, [client]);
}
